import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService, usersService, casesService, samplesService, tracesService, locationsService, staticDataService } from '../services/apiService';
import { getToken, removeToken, removeRefreshToken } from '../config/api';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

// Use API mode (set to false to use localStorage for testing)
// React reads env vars at build time, so we need to check the actual value
// You can also set localStorage.setItem('FORCE_API_MODE', 'true') in browser console to force API mode
const getUseApi = () => {
  // Check localStorage first (for runtime override)
  const localStorageOverride = localStorage.getItem('FORCE_API_MODE');
  if (localStorageOverride !== null) {
    return localStorageOverride === 'true';
  }
  
  // Check env var (read at build time)
  const envValue = process.env.REACT_APP_USE_API;
  return envValue === 'true' || envValue === true;
};

const USE_API = getUseApi();

// Debug: Log to verify env var is being read
console.log('=== API MODE CONFIGURATION ===');
console.log('REACT_APP_USE_API env:', process.env.REACT_APP_USE_API);
console.log('Type:', typeof process.env.REACT_APP_USE_API);
console.log('localStorage FORCE_API_MODE:', localStorage.getItem('FORCE_API_MODE'));
console.log('Final USE_API value:', USE_API);
console.log('API Mode is:', USE_API ? 'ENABLED' : 'DISABLED (using localStorage)');

export const AppProvider = ({ children }) => {
  // Authentication state - initialize from localStorage if available
  const getInitialUser = () => {
    try {
      const savedUser = localStorage.getItem('currentUser');
      if (savedUser) {
        return JSON.parse(savedUser);
      }
    } catch (error) {
      console.error('Error parsing saved user:', error);
    }
    return null;
  };

  const [currentUser, setCurrentUser] = useState(getInitialUser);
  const [loading, setLoading] = useState(true);

  // Initialize static data from localStorage if available
  const getInitialRegions = () => {
    try {
      const savedRegions = localStorage.getItem('fingerprint_regions');
      if (savedRegions) {
        return JSON.parse(savedRegions);
      }
    } catch (error) {
      console.error('Error parsing saved regions:', error);
    }
    return {};
  };

  const getInitialLocations = () => {
    try {
      const savedLocations = localStorage.getItem('fingerprint_locations');
      if (savedLocations) {
        return JSON.parse(savedLocations);
      }
    } catch (error) {
      console.error('Error parsing saved locations:', error);
    }
    return [];
  };

  // Data storage
  const [users, setUsers] = useState([]);
  const [cases, setCases] = useState([]);
  const [samples, setSamples] = useState([]);
  const [traces, setTraces] = useState([]);
  const [locations, setLocations] = useState(getInitialLocations);
  const [regions, setRegions] = useState(getInitialRegions);
  
  // Pagination state
  const [usersPagination, setUsersPagination] = useState({ currentPage: 1, totalPages: 0, totalItems: 0, itemsPerPage: 10 });
  const [casesPagination, setCasesPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [locationsPagination, setLocationsPagination] = useState({ currentPage: 1, totalPages: 0, totalItems: 0, itemsPerPage: 10 });

  // Loading states for different data types
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingCases, setLoadingCases] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingSamples, setLoadingSamples] = useState(false);
  const [loadingTraces, setLoadingTraces] = useState(false);

  // Load initial data
  useEffect(() => {
    const initializeData = async () => {
      try {
        if (USE_API) {
          // Load from API
          const token = getToken();
          if (token) {
            try {
              // Try to get current user from API
              const userData = await authService.getCurrentUser();
              // Handle different response structures
              const user = userData?.data || userData;
              if (user) {
                setCurrentUser(user);
                // Save user to localStorage for persistence
                localStorage.setItem('currentUser', JSON.stringify(user));
              }
            } catch (error) {
              console.error('Failed to get current user:', error);
              // Clear invalid token and user data
              removeToken();
              removeRefreshToken();
              localStorage.removeItem('currentUser');
              setCurrentUser(null);
            }
          } else {
            // No token, clear any saved user
            localStorage.removeItem('currentUser');
            setCurrentUser(null);
          }

          // Regions are not loaded here - they will be loaded on demand
          // Only in UserManagement and LocationManagement components
          // This improves performance by not loading unnecessary data

          // Don't load data on refresh - load only when needed (lazy loading)
          // This improves performance significantly
          if (!token) {
            // No token, set empty arrays
            setUsers([]);
            setCases([]);
            setSamples([]);
            setTraces([]);
            setLocations([]);
          }
          // Data will be loaded when user navigates to specific pages:
          // - Cases: loaded in CaseManagement component
          // - Users: loaded in UserManagement component
          // - Locations: loaded in LocationManagement component
          // - Samples: loaded in SampleManagement component
          // - Traces: loaded in TraceManagement component
        } else {
          // Load from localStorage (for testing)
          const savedUsers = localStorage.getItem('fingerprint_users');
          if (savedUsers) {
            setUsers(JSON.parse(savedUsers));
          } else {
            // Default users for testing
            setUsers([
              {
                id: '1',
                fullName: 'أحمد محمد علي',
                username: 'admin',
                password: 'admin',
                role: 'System Administrator',
                region: 'الرياض',
                city: 'الرياض',
                rank: 'عقيد',
                position: 'مدير النظام'
              },
              {
                id: '2',
                fullName: 'فاطمة أحمد',
                username: 'examiner',
                password: 'examiner',
                role: 'Examiner',
                region: 'مكة المكرمة',
                city: 'جدة',
                rank: 'رائد',
                position: 'خبير بصمات'
              },
              {
                id: '3',
                fullName: 'محمد خالد',
                username: 'supervisor',
                password: 'supervisor',
                role: 'Supervisor',
                region: 'الشرقية',
                city: 'الدمام',
                rank: 'مقدم',
                position: 'مشرف قسم'
              },
              {
                id: '4',
                fullName: 'سارة محمود',
                username: 'readonly',
                password: 'readonly',
                role: 'Read-Only User',
                region: 'المدينة المنورة',
                city: 'المدينة المنورة',
                rank: 'نقيب',
                position: 'موظف'
              }
            ]);
          }

          const savedCases = localStorage.getItem('fingerprint_cases');
          if (savedCases) {
            setCases(JSON.parse(savedCases));
          }

          const savedSamples = localStorage.getItem('fingerprint_samples');
          if (savedSamples) {
            setSamples(JSON.parse(savedSamples));
          }

          const savedTraces = localStorage.getItem('fingerprint_traces');
          if (savedTraces) {
            setTraces(JSON.parse(savedTraces));
          }

          // Load regions from local file
          const { saudiRegions } = require('../data/saudiRegions');
          setRegions(saudiRegions);

          // Check if user is logged in
          const savedUser = localStorage.getItem('currentUser');
          if (savedUser) {
            setCurrentUser(JSON.parse(savedUser));
          }
        }
      } catch (error) {
        console.error('Error initializing data:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  // Load users from API (optionally filtered by location)
  const loadUsers = async (page = 1, append = false, locationId = null) => {
    if (USE_API) {
      setLoadingUsers(true);
      try {
        const usersResponse = await usersService.getAll(page, 10, locationId).catch(err => {
          console.error('Error loading users from API:', err);
          return { data: [], pagination: { page: 1, limit: 10, total: 0, totalPages: 0 } };
        });
        
        // Handle paginated response for users
        let usersData = [];
        if (usersResponse && usersResponse.pagination) {
          usersData = usersResponse.data || [];
          if (append) {
            setUsers(prev => [...prev, ...usersData]);
          } else {
            setUsers(usersData);
          }
          setUsersPagination({
            currentPage: usersResponse.pagination.currentPage || page,
            totalPages: usersResponse.pagination.totalPages || 0,
            totalItems: usersResponse.pagination.totalItems || 0,
            itemsPerPage: usersResponse.pagination.itemsPerPage || 10
          });
        } else if (Array.isArray(usersResponse)) {
          usersData = usersResponse;
          if (append) {
            setUsers(prev => [...prev, ...usersData]);
          } else {
            setUsers(usersData);
          }
        } else if (usersResponse && usersResponse.data) {
          usersData = usersResponse.data;
          if (append) {
            setUsers(prev => [...prev, ...usersData]);
          } else {
            setUsers(usersData);
          }
        } else {
          if (!append) {
            setUsers([]);
          }
        }
      } catch (error) {
        console.error('Error loading users from API:', error);
        if (!append) {
          setUsers([]);
        }
      } finally {
        setLoadingUsers(false);
      }
    }
  };

  // Load cases from API (without users, samples, and traces to improve performance)
  const loadAllData = async (page = 1, append = false, filters = {}) => {
    setLoadingCases(true);
    try {
      // Load cases only (without samples and traces to improve performance)
      const casesResponse = await casesService.getAll(page, 10, filters).catch(err => {
        console.error('Error loading cases from API:', err);
        // Re-throw validation errors (422) so they can be handled by the caller
        if (err.response && err.response.status === 422) {
          throw err;
        }
        return { 
          data: [], 
          pagination: { page: 1, limit: 10, total: 0, totalPages: 0, hasMore: false } 
        };
      });

      if (casesResponse && casesResponse.data) {
        const casesData = Array.isArray(casesResponse.data) ? casesResponse.data : (casesResponse.data.data || []);
        
        if (append) {
          setCases(prev => [...prev, ...casesData]);
        } else {
          setCases(casesData || []);
          // Clear samples and traces when loading new cases (they will be loaded on demand)
          setSamples([]);
          setTraces([]);
        }

        if (casesResponse.pagination) {
          setCasesPagination({
            page: casesResponse.pagination.currentPage || casesResponse.pagination.page || page,
            limit: casesResponse.pagination.itemsPerPage || casesResponse.pagination.limit || 10,
            total: casesResponse.pagination.totalItems || casesResponse.pagination.total || 0,
            totalItems: casesResponse.pagination.totalItems || casesResponse.pagination.total || 0,
            totalPages: casesResponse.pagination.totalPages || 0,
            hasMore: casesResponse.pagination.hasMore || false,
          });
        }
      } else {
        if (!append) {
          setCases([]);
          setSamples([]);
          setTraces([]);
        }
      }
    } catch (error) {
      console.error('Error loading cases from API:', error);
      // Set empty arrays on error to prevent crashes (only if not appending)
      if (!append) {
        setCases([]);
        setSamples([]);
        setTraces([]);
      }
    } finally {
      setLoadingCases(false);
    }
  };

  // Persist data to localStorage (only if not using API)
  useEffect(() => {
    if (!USE_API) {
      localStorage.setItem('fingerprint_users', JSON.stringify(users));
    }
  }, [users]);

  useEffect(() => {
    if (!USE_API) {
      localStorage.setItem('fingerprint_cases', JSON.stringify(cases));
    }
  }, [cases]);

  useEffect(() => {
    if (!USE_API) {
      localStorage.setItem('fingerprint_samples', JSON.stringify(samples));
    }
  }, [samples]);

  useEffect(() => {
    if (!USE_API) {
      localStorage.setItem('fingerprint_traces', JSON.stringify(traces));
    }
  }, [traces]);

  // Generate case number
  const generateCaseNumber = () => {
    const year = new Date().getFullYear();
    const caseCount = cases.filter(c => c.caseNumber?.startsWith(year.toString())).length;
    return `${year}-${String(caseCount + 1).padStart(3, '0')}`;
  };

  // Generate sample number
  const generateSampleNumber = (caseNumber) => {
    const caseSamples = samples.filter(s => s.caseId === cases.find(c => c.caseNumber === caseNumber)?.id);
    const sequence = caseSamples.length + 1;
    return `${caseNumber} / S-${String(sequence).padStart(2, '0')}`;
  };

  // Generate trace number
  const generateTraceNumber = (caseNumber, sampleNumber) => {
    const sample = samples.find(s => s.sampleNumber === sampleNumber);
    if (!sample) return '';
    const sampleTraces = traces.filter(t => t.sampleId === sample.id);
    const sequence = sampleTraces.length + 1;
    return `${caseNumber} / ${sampleNumber} / FP-${String(sequence).padStart(2, '0')}`;
  };

  // Login function
  const login = async (username, password) => {
    try {
      if (USE_API) {
        try {
          const user = await authService.login(username, password);
          if (user) {
            setCurrentUser(user);
            // Save user to localStorage for persistence across page refreshes
            localStorage.setItem('currentUser', JSON.stringify(user));
            // Load cases immediately after login (user usually goes to cases page)
            await loadAllData(1);
            // Load users from the same location for case assignment
            if (user.locationId || user.location_id) {
              const locationId = user.locationId || user.location_id;
              await loadUsers(1, false, locationId);
            } else {
              // If user has no location, load all users (for System Administrator or users without location)
              await loadUsers(1, false, null);
            }
            return true;
          } else {
            return false;
          }
        } catch (apiError) {
          console.error('API Login error:', apiError);
          throw apiError; // Re-throw to be caught by outer catch
        }
      } else {
        // LocalStorage mode
        const user = users.find(u => u.username === username && u.password === password);
        if (user) {
          setCurrentUser(user);
          localStorage.setItem('currentUser', JSON.stringify(user));
          return true;
        }
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      if (USE_API) {
        await authService.logout();
      }
      // Always remove user from localStorage regardless of mode
      localStorage.removeItem('currentUser');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setCurrentUser(null);
    }
  };

  // User management functions
  const addUser = async (userData) => {
    if (USE_API) {
      try {
        const newUser = await usersService.create(userData);
        setUsers([...users, newUser]);
        return newUser;
      } catch (error) {
        // Re-throw error to be caught by component's catch block
        throw error;
      }
    } else {
      const newUser = {
        id: Date.now().toString(),
        ...userData
      };
      setUsers([...users, newUser]);
      return newUser;
    }
  };

  const updateUser = async (id, userData) => {
    if (USE_API) {
      try {
        const response = await usersService.update(id, userData);
        const updatedUser = response.data || response;
        setUsers(users.map(u => u.id === id ? updatedUser : u));
        return updatedUser;
      } catch (error) {
        // Re-throw error to be caught by component's catch block
        throw error;
      }
    } else {
      setUsers(users.map(u => u.id === id ? { ...u, ...userData } : u));
    }
  };

  const deleteUser = async (id) => {
    if (USE_API) {
      await usersService.delete(id);
      setUsers(users.filter(u => u.id !== id));
    } else {
      setUsers(users.filter(u => u.id !== id));
    }
  };

  // Case management functions
  const addCase = async (caseData) => {
    console.log(caseData)

    if (USE_API) {
      try {
        // Backend generates case number automatically with new format (YY-LLLL-NNNNN)
        // Don't include caseNumber in request - backend will generate it
        const response = await casesService.create(caseData);
        const newCase = response.data || response;
        setCases([...cases, newCase]);
        return newCase;
      } catch (error) {
        // Re-throw error to be caught by component's catch block
        throw error;
      }
    } else {
      // Only generate case number locally when not using API
      const caseNumber = generateCaseNumber();
      const newCaseData = {
        ...caseData,
        caseNumber,
        createdAt: new Date().toISOString()
      };
      const newCase = {
        id: Date.now().toString(),
        ...newCaseData
      };
      setCases([...cases, newCase]);
      return newCase;
    }
  };

  const updateCase = async (id, caseData) => {
    if (USE_API) {
      try {
        const response = await casesService.update(id, caseData);
        const updatedCase = response.data || response;
        setCases(cases.map(c => c.id === id ? updatedCase : c));
        return updatedCase;
      } catch (error) {
        // Re-throw error to be caught by component's catch block
        throw error;
      }
    } else {
      setCases(cases.map(c => c.id === id ? { ...c, ...caseData } : c));
    }
  };

  const deleteCase = async (id) => {
    if (USE_API) {
      await casesService.delete(id);
      setCases(cases.filter(c => c.id !== id));
      // Also delete associated samples and traces
      const caseSamples = samples.filter(s => s.caseId === id);
      const sampleIds = caseSamples.map(s => s.id);
      setSamples(samples.filter(s => s.caseId !== id));
      setTraces(traces.filter(t => !sampleIds.includes(t.sampleId)));
    } else {
      setCases(cases.filter(c => c.id !== id));
      const caseSamples = samples.filter(s => s.caseId === id);
      const sampleIds = caseSamples.map(s => s.id);
      setSamples(samples.filter(s => s.caseId !== id));
      setTraces(traces.filter(t => !sampleIds.includes(t.sampleId)));
    }
  };

  const isFormData = (val) => (typeof FormData !== 'undefined' && val instanceof FormData);

  const getFieldValue = (data, key) => {
    if (isFormData(data)) return data.get(key);
    return data ? data[key] : undefined;
  };

  const formDataToObject = (fd) => {
    const obj = {};
    try {
      for (const [k, v] of fd.entries()) {
        obj[k] = v;
      }
    } catch (e) {
      // ignore
    }
    return obj;
  };

  // Sample management functions
  const addSample = async (sampleData) => {
    try {
      const caseIdValue = String(getFieldValue(sampleData, 'caseId'));
      const caseObj = cases.find(c => String(c.id) === caseIdValue);
      if (!caseObj) {
        throw new Error('القضية غير موجودة');
      }

      if (USE_API) {
        const created = await samplesService.create(sampleData);
        // If API omits image in response (legacy), fall back to local image for object payloads
        const newSample = {
          ...created,
          sampleImage: created?.sampleImage ?? (!isFormData(sampleData) ? (sampleData?.sampleImage ?? null) : null),
        };
        setSamples([...samples, newSample]);
        return newSample;
      } else {
        const sampleNumber = generateSampleNumber(caseObj.caseNumber);
        const sampleObj = isFormData(sampleData) ? formDataToObject(sampleData) : sampleData;
        const newSample = {
          id: Date.now().toString(),
          ...sampleObj,
          sampleNumber,
          createdAt: new Date().toISOString()
        };
        setSamples([...samples, newSample]);
        return newSample;
      }
    } catch (error) {
      console.error('Error adding sample:', error);
      throw error;
    }
  };

  const updateSample = async (id, sampleData) => {
    try {
      if (USE_API) {
        const updatedSample = await samplesService.update(id, sampleData);
        setSamples(samples.map(s => String(s.id) === String(id) ? updatedSample : s));
        return updatedSample;
      } else {
        const sampleObj = isFormData(sampleData) ? formDataToObject(sampleData) : sampleData;
        setSamples(samples.map(s => String(s.id) === String(id) ? { ...s, ...sampleObj } : s));
      }
    } catch (error) {
      console.error('Error updating sample:', error);
      throw error;
    }
  };

  const deleteSample = async (id) => {
    if (USE_API) {
      await samplesService.delete(id);
      setSamples(samples.filter(s => s.id !== id));
      setTraces(traces.filter(t => t.sampleId !== id));
    } else {
      setSamples(samples.filter(s => s.id !== id));
      setTraces(traces.filter(t => t.sampleId !== id));
    }
  };

  // Trace management functions
  const addTrace = async (traceData) => {
    try {
      const sampleIdValue = String(getFieldValue(traceData, 'sampleId'));
      const sample = samples.find(s => String(s.id) === sampleIdValue);
      if (!sample) {
        throw new Error('العينة غير موجودة');
      }
      
      const caseObj = cases.find(c => String(c.id) === String(sample.caseId));
      if (!caseObj) {
        throw new Error('القضية غير موجودة');
      }

      if (USE_API) {
        const created = await tracesService.create(traceData);
        // If API omits image in response (legacy), fall back to local image for object payloads
        const newTrace = {
          ...created,
          traceImage: created?.traceImage ?? (!isFormData(traceData) ? (traceData?.traceImage ?? null) : null),
        };
        setTraces([...traces, newTrace]);
        return newTrace;
      } else {
        const traceNumber = generateTraceNumber(caseObj.caseNumber, sample.sampleNumber);
        const traceObj = isFormData(traceData) ? formDataToObject(traceData) : traceData;
        const newTrace = {
          id: Date.now().toString(),
          ...traceObj,
          traceNumber,
          createdAt: new Date().toISOString()
        };
        setTraces([...traces, newTrace]);
        return newTrace;
      }
    } catch (error) {
      console.error('Error adding trace:', error);
      throw error;
    }
  };

  const updateTrace = async (id, traceData) => {
    try {
      if (USE_API) {
        const updatedTrace = await tracesService.update(id, traceData);
        setTraces(traces.map(t => String(t.id) === String(id) ? updatedTrace : t));
        return updatedTrace;
      } else {
        const traceObj = isFormData(traceData) ? formDataToObject(traceData) : traceData;
        setTraces(traces.map(t => String(t.id) === String(id) ? { ...t, ...traceObj } : t));
      }
    } catch (error) {
      console.error('Error updating trace:', error);
      throw error;
    }
  };

  const deleteTrace = async (id) => {
    if (USE_API) {
      await tracesService.delete(id);
      setTraces(traces.filter(t => t.id !== id));
    } else {
      setTraces(traces.filter(t => t.id !== id));
    }
  };

  // Load samples for a specific case
  const loadSamplesByCase = useCallback(async (caseId) => {
    if (USE_API) {
      setLoadingSamples(true);
      try {
        const samplesData = await samplesService.getByCase(caseId);
        const samplesArray = Array.isArray(samplesData) ? samplesData : (samplesData.data || []);
        // Update samples for this case only, keep others
        setSamples(prev => {
          const filtered = prev.filter(s => String(s.caseId) !== String(caseId));
          return [...filtered, ...samplesArray];
        });
      } catch (error) {
        console.error('Error loading samples from API:', error);
        // Remove samples for this case on error
        setSamples(prev => prev.filter(s => String(s.caseId) !== String(caseId)));
      } finally {
        setLoadingSamples(false);
      }
    }
  }, []);

  // Load traces for a specific case (all traces for all samples in the case)
  const loadTracesByCase = useCallback(async (caseId) => {
    if (USE_API) {
      setLoadingTraces(true);
      try {
        const tracesData = await tracesService.getByCase(caseId);
        const tracesArray = Array.isArray(tracesData) ? tracesData : (tracesData.data || []);
        // Update traces for this case only, keep others
        setTraces(prev => {
          const filtered = prev.filter(t => String(t.caseId) !== String(caseId));
          return [...filtered, ...tracesArray];
        });
      } catch (error) {
        console.error('Error loading traces from API:', error);
        // Remove traces for this case on error
        setTraces(prev => prev.filter(t => String(t.caseId) !== String(caseId)));
      } finally {
        setLoadingTraces(false);
      }
    }
  }, []);

  // Load traces for a specific sample
  const loadTracesBySample = useCallback(async (sampleId) => {
    if (USE_API) {
      setLoadingTraces(true);
      try {
        const tracesData = await tracesService.getBySample(sampleId);
        const tracesArray = Array.isArray(tracesData) ? tracesData : (tracesData.data || []);
        // Update traces for this sample only, keep others
        setTraces(prev => {
          const filtered = prev.filter(t => String(t.sampleId) !== String(sampleId));
          return [...filtered, ...tracesArray];
        });
      } catch (error) {
        console.error('Error loading traces from API:', error);
        // Remove traces for this sample on error
        setTraces(prev => prev.filter(t => String(t.sampleId) !== String(sampleId)));
      } finally {
        setLoadingTraces(false);
      }
    }
  }, []);

  // Load regions (static data) - only when needed
  const loadRegions = async () => {
    // Check if already loaded in state
    if (Object.keys(regions).length > 0) {
      // Already loaded, no need to reload
      return;
    }
    
    // Check if available in localStorage
    const savedRegions = localStorage.getItem('fingerprint_regions');
    if (savedRegions) {
      try {
        const regionsData = JSON.parse(savedRegions);
        if (Object.keys(regionsData).length > 0) {
          setRegions(regionsData);
          return; // Use cached data
        }
      } catch (error) {
        console.error('Error parsing saved regions:', error);
      }
    }
    
    if (USE_API) {
      try {
        const regionsData = await staticDataService.getRegions();
        setRegions(regionsData);
        // Save to localStorage for persistence
        localStorage.setItem('fingerprint_regions', JSON.stringify(regionsData));
      } catch (error) {
        console.error('Failed to load regions from API:', error);
        // Fallback to local data
        const { saudiRegions } = require('../data/saudiRegions');
        setRegions(saudiRegions);
        localStorage.setItem('fingerprint_regions', JSON.stringify(saudiRegions));
      }
    } else {
      // Load from local file
      const { saudiRegions } = require('../data/saudiRegions');
      setRegions(saudiRegions);
      localStorage.setItem('fingerprint_regions', JSON.stringify(saudiRegions));
    }
  };

  // Location management functions
  const loadLocations = async (page = 1, append = false) => {
    if (USE_API) {
      setLoadingLocations(true);
      try {
        const response = await locationsService.getAll(page, 10).catch(err => {
          console.error('Error loading locations from API:', err);
          return { data: [], pagination: { currentPage: 1, totalPages: 0, totalItems: 0, itemsPerPage: 10 } };
        });
        
        // Handle paginated response for locations
        let locationsData = [];
        if (response && response.pagination) {
          locationsData = response.data || [];
          if (append) {
            setLocations(prev => [...prev, ...locationsData]);
          } else {
            setLocations(locationsData);
          }
          setLocationsPagination({
            currentPage: response.pagination.currentPage || page,
            totalPages: response.pagination.totalPages || 0,
            totalItems: response.pagination.totalItems || 0,
            itemsPerPage: response.pagination.itemsPerPage || 10
          });
        } else if (Array.isArray(response)) {
          locationsData = response;
          if (append) {
            setLocations(prev => [...prev, ...locationsData]);
          } else {
            setLocations(locationsData);
          }
        } else if (response && response.data) {
          locationsData = response.data;
          if (append) {
            setLocations(prev => [...prev, ...locationsData]);
          } else {
            setLocations(locationsData);
          }
        } else {
          if (!append) {
            setLocations([]);
          }
        }
      } catch (error) {
        console.error('Error loading locations from API:', error);
        if (!append) {
          setLocations([]);
        }
      } finally {
        setLoadingLocations(false);
      }
    } else {
      // Load from localStorage (for testing)
      const savedLocations = localStorage.getItem('fingerprint_locations');
      if (savedLocations) {
        try {
          const locationsData = JSON.parse(savedLocations);
          setLocations(locationsData);
        } catch (error) {
          console.error('Error parsing saved locations:', error);
          setLocations([]);
        }
      } else {
        setLocations([]);
      }
    }
  };

  const addLocation = async (locationData) => {
    if (USE_API) {
      try {
        const newLocation = await locationsService.create({
          name: locationData.name,
          region: locationData.region,
          city: locationData.city,
          supervisorId: locationData.supervisorId || null,
        });
        const updatedLocations = [...locations, newLocation];
        setLocations(updatedLocations);
        // Save to localStorage for persistence
        localStorage.setItem('fingerprint_locations', JSON.stringify(updatedLocations));
        return newLocation;
      } catch (error) {
        // Re-throw error to be caught by component's catch block
        throw error;
      }
    } else {
      const newLocation = {
        id: Date.now().toString(),
        ...locationData,
        supervisor: locationData.supervisorId ? users.find(u => u.id === locationData.supervisorId) : null,
      };
      const updatedLocations = [...locations, newLocation];
      setLocations(updatedLocations);
      localStorage.setItem('fingerprint_locations', JSON.stringify(updatedLocations));
      return newLocation;
    }
  };

  const updateLocation = async (id, locationData) => {
    if (USE_API) {
      try {
        const response = await locationsService.update(id, {
          name: locationData.name,
          region: locationData.region,
          city: locationData.city,
          supervisorId: locationData.supervisorId || null,
        });
        const updatedLocation = response.data || response;
        const updatedLocations = locations.map(l => l.id === id ? updatedLocation : l);
        setLocations(updatedLocations);
        // Save to localStorage for persistence
        localStorage.setItem('fingerprint_locations', JSON.stringify(updatedLocations));
        return updatedLocation;
      } catch (error) {
        // Re-throw error to be caught by component's catch block
        throw error;
      }
    } else {
      const updatedLocation = {
        ...locations.find(l => l.id === id),
        ...locationData,
        supervisor: locationData.supervisorId ? users.find(u => u.id === locationData.supervisorId) : null,
      };
      const updatedLocations = locations.map(l => l.id === id ? updatedLocation : l);
      setLocations(updatedLocations);
      localStorage.setItem('fingerprint_locations', JSON.stringify(updatedLocations));
    }
  };

  const deleteLocation = async (id) => {
    if (USE_API) {
      await locationsService.delete(id);
      const updatedLocations = locations.filter(l => l.id !== id);
      setLocations(updatedLocations);
      // Save to localStorage for persistence
      localStorage.setItem('fingerprint_locations', JSON.stringify(updatedLocations));
    } else {
      const updatedLocations = locations.filter(l => l.id !== id);
      setLocations(updatedLocations);
      localStorage.setItem('fingerprint_locations', JSON.stringify(updatedLocations));
    }
  };

  // Permission checks
  const canManageUsers = () => {
    return currentUser?.role === 'System Administrator';
  };

  const canCreateCase = () => {
    // System Administrator cannot create cases - only manage users and view audit logs
    return ['Examiner', 'Region Supervisor', 'Location Supervisor'].includes(currentUser?.role);
  };

  const canEditCase = () => {
    return ['System Administrator', 'Examiner', 'Region Supervisor', 'Location Supervisor'].includes(currentUser?.role);
  };

  const canDeleteCase = () => {
    // Only System Administrator and Region Supervisor can delete cases
    return ['System Administrator', 'Region Supervisor'].includes(currentUser?.role);
  };

  const canEditSample = () => {
    return ['System Administrator', 'Examiner', 'Region Supervisor', 'Location Supervisor'].includes(currentUser?.role);
  };

  const canEditTrace = () => {
    return ['System Administrator', 'Examiner', 'Region Supervisor', 'Location Supervisor'].includes(currentUser?.role);
  };

  // Get assigned cases for current user
  const getAssignedCases = () => {
    if (!currentUser) return [];
    if (currentUser.role === 'System Administrator') return cases;
    return cases.filter(c => c.assignedUsers?.includes(currentUser.id));
  };

  const value = {
    currentUser,
    users,
    cases,
    samples,
    traces,
    locations,
    regions,
    loading,
    loadingUsers,
    loadingCases,
    loadingLocations,
    loadingSamples,
    loadingTraces,
    usersPagination,
    casesPagination,
    locationsPagination,
    login,
    logout,
    addUser,
    updateUser,
    deleteUser,
    addCase,
    updateCase,
    deleteCase,
    addSample,
    updateSample,
    deleteSample,
    addTrace,
    updateTrace,
    deleteTrace,
    addLocation,
    updateLocation,
    deleteLocation,
    loadLocations,
    loadUsers,
    loadRegions,
    loadSamplesByCase,
    loadTracesByCase,
    loadTracesBySample,
    canManageUsers,
    canCreateCase,
    canEditCase,
    canDeleteCase,
    canEditSample,
    canEditTrace,
    getAssignedCases,
    loadAllData
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
